/**
 * Ticket detail — the full picture of one feedback session:
 *  - status control + AI enrichment (title / summary / category / sentiment /
 *    quality score + reasons),
 *  - page + device metadata,
 *  - conversation transcript,
 *  - action timeline, console logs, captured JS errors,
 *  - screenshot attachment + rrweb replay player,
 *  - internal comment thread + closed-loop reply panel.
 *
 * Subscribes to per-ticket realtime updates so AI enrichment / status changes
 * land without a refresh.
 *
 * Visual: Claude Design ticket detail (`dash-detail.jsx` + `.detail-*` rules) —
 * a scrolling two-column layout: an enrichment header, session replay, and a
 * tabbed conversation/console/timeline card on the left, a 360px side rail
 * (status controls · context meta · screenshot · comments · reply) on the right.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import {
  TICKET_STATUSES,
  type Attachment,
  type TicketStatus,
  type Ticket,
} from '@hotline/contracts';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import { useTicketEvents } from '../../realtime/useRealtime.js';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  QualityScore,
  Select,
  Severity,
  Spinner,
  StatusPill,
  cn,
  type SeverityLevel,
} from '../../components/ui/index.js';
import { Icon } from '../../components/ui/icons.js';
import { ReplayPlayer } from '../../components/ReplayPlayer.js';
import { CommentThread } from '../../components/CommentThread.js';
import { ReplyPanel } from '../../components/ReplyPanel.js';
import { useAsync, toMessage } from '../../lib/useAsync.js';

/** Maps an AI sentiment to a Severity-style tone (text token + dot). */
const SENTIMENT_TONE: Record<string, string> = {
  positive: 'text-ok',
  negative: 'text-danger',
  neutral: 'text-muted',
};

/** Quality-score bar color: green when strong, amber mid, red when weak. */
function qualityTone(score: number): 'ok' | 'warn' | 'danger' {
  if (score >= 0.75) return 'ok';
  if (score >= 0.5) return 'warn';
  return 'danger';
}

/** Heuristic mapping of an AI category to a design Severity level (visual only). */
function categorySeverity(category: string | null | undefined): SeverityLevel | null {
  if (!category) return null;
  const c = category.toLowerCase();
  if (c.includes('crash') || c.includes('error') || c.includes('outage'))
    return 'critical';
  if (c.includes('bug') || c.includes('fail')) return 'high';
  if (c.includes('confus') || c.includes('ux') || c.includes('slow'))
    return 'medium';
  return 'low';
}

const TABS = [
  { id: 'convo', label: 'Conversation', icon: 'msg' as const },
  { id: 'logs', label: 'Console', icon: 'code' as const },
  { id: 'timeline', label: 'Timeline', icon: 'clock' as const },
];
type TabId = (typeof TABS)[number]['id'];

export default function TicketDetailPage(): ReactElement {
  const { ticketId = '' } = useParams();
  const { client } = useAuth();
  const { query } = useOrgScope();

  const ticket = useAsync<Ticket>(
    () => client.api.getTicket(ticketId, query),
    [client, ticketId, query.orgId],
  );

  // Live updates for this specific ticket.
  useTicketEvents(ticketId, {
    onUpdate: (updated) => ticket.setData(updated),
  });

  const screenshot = useMemo(
    () => ticket.data?.attachments?.find((a) => a.kind === 'screenshot'),
    [ticket.data],
  );
  const replay = useMemo(
    () => ticket.data?.attachments?.find((a) => a.kind === 'replay'),
    [ticket.data],
  );
  const files = useMemo(
    () => ticket.data?.attachments?.filter((a) => a.kind === 'file') ?? [],
    [ticket.data],
  );

  const [tab, setTab] = useState<TabId>('convo');

  if (ticket.loading) return <Spinner label="Loading ticket…" />;
  if (ticket.error) return <Alert>{ticket.error}</Alert>;
  if (!ticket.data) return <Alert>Ticket not found</Alert>;

  const t = ticket.data;
  const title = t.aiTitle || `Ticket ${t.id.slice(0, 8)}`;
  const sevLevel = categorySeverity(t.aiCategory);

  const changeStatus = async (status: TicketStatus): Promise<void> => {
    try {
      const updated = await client.api.updateTicket(t.id, { status }, query);
      ticket.setData(updated);
    } catch (err) {
      ticket.setData((prev) => prev as Ticket); // keep prior on failure
      // surface via a transient alert
      window.alert(toMessage(err));
    }
  };

  const hasLogs =
    (t.consoleLogs && t.consoleLogs.length > 0) ||
    (t.errors && t.errors.length > 0);
  const hasTimeline = t.actions && t.actions.length > 0;

  return (
    <section className="flex flex-col gap-[18px]">
      {/* ── Top bar: back · crumb · status · actions ─────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to="/org/tickets"
          className={cn(
            'inline-flex h-[30px] items-center gap-1.5 rounded-sm border border-border ' +
              'pl-[7px] pr-2.5 text-[12.5px] font-semibold text-muted transition ' +
              'hover:border-strong hover:text-text ' +
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&_svg]:size-[15px]',
          )}
        >
          <Icon.arrowLeft />
          Tickets
        </Link>
        <span className="mono whitespace-nowrap text-xs text-faint">
          {t.id.slice(0, 12)}
        </span>
        <StatusPill status={t.status} />
        <span className="flex-1" />
        {t.pageUrl ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigator.clipboard?.writeText(t.pageUrl ?? '')}
          >
            <Icon.link />
            Copy link
          </Button>
        ) : null}
      </div>

      {/* ── Two columns: scrolling main + 360px side rail ────────────── */}
      <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* ── Main column ─────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-[18px]">
          {/* AI enrichment header */}
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3.5">
              <h1 className="flex-1 text-[22px] font-extrabold leading-[1.2] tracking-[-0.015em] text-text">
                {title}
              </h1>
              {t.aiSummary || t.aiCategory ? (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-soft px-[9px] py-[3px] text-[11px] font-bold text-primary [&_svg]:size-3">
                  <Icon.sparkle />
                  AI-enriched
                </span>
              ) : null}
            </div>
            {t.aiSummary ? (
              <p className="text-sm leading-[1.55] text-muted">{t.aiSummary}</p>
            ) : (
              <p className="text-sm text-muted">Not enriched yet.</p>
            )}
            <div className="flex flex-wrap items-center gap-[7px]">
              {sevLevel ? <Severity level={sevLevel} /> : null}
              <Badge tone="primary" square>
                {t.channel}
              </Badge>
              {t.aiCategory ? <Badge square>{t.aiCategory}</Badge> : null}
              {t.aiSentiment ? (
                <Badge tone="warn" square>
                  <Icon.frown />
                  <span
                    className={cn(
                      'capitalize',
                      SENTIMENT_TONE[t.aiSentiment] ?? 'text-muted',
                    )}
                  >
                    {t.aiSentiment}
                  </span>
                </Badge>
              ) : null}
              {typeof t.qualityScore === 'number' ? (
                <span className="ml-auto">
                  <QualityScore
                    value={t.qualityScore}
                    tone={qualityTone(t.qualityScore)}
                    label={`${Math.round(t.qualityScore * 100)}%`}
                  />
                </span>
              ) : null}
            </div>
            {t.qualityReasons && t.qualityReasons.length > 0 ? (
              <ul className="ml-4 list-disc text-[12.5px] leading-[1.5] text-muted">
                {t.qualityReasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            ) : null}
          </div>

          {/* Session replay */}
          <ReplayPlayer url={replay?.url} />

          {/* Conversation / Console / Timeline tabs */}
          <Card className="overflow-hidden">
            <div
              role="tablist"
              aria-label="Ticket detail"
              className="flex gap-0.5 border-b border-border px-1"
            >
              {TABS.map((tb) => {
                const I = Icon[tb.icon];
                const on = tab === tb.id;
                const count =
                  tb.id === 'logs'
                    ? (t.consoleLogs?.length ?? 0) + (t.errors?.length ?? 0)
                    : tb.id === 'timeline'
                      ? (t.actions?.length ?? 0)
                      : (t.transcript?.length ?? 0);
                return (
                  <button
                    key={tb.id}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    onClick={() => setTab(tb.id)}
                    className={cn(
                      'relative flex items-center gap-[7px] px-[13px] pb-3 pt-[11px] text-[13px] font-semibold transition [&_svg]:size-[15px]',
                      on
                        ? 'text-text after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-[2px] after:bg-primary'
                        : 'text-muted hover:text-text',
                    )}
                  >
                    <I />
                    {tb.label}
                    {count > 0 ? (
                      <span className="rounded-full bg-surface-3 px-1.5 py-px text-[11px] font-bold text-muted">
                        {count}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* All panels stay mounted (so captured data is searchable /
                printable); only the active one is shown. */}
            <div className="p-[18px]">
              <div role="tabpanel" hidden={tab !== 'convo'}>
                <Conversation ticket={t} />
              </div>
              <div role="tabpanel" hidden={tab !== 'logs'}>
                <Console ticket={t} />
              </div>
              <div role="tabpanel" hidden={tab !== 'timeline'}>
                <Timeline ticket={t} />
              </div>
            </div>
          </Card>

          {/* Fallback hint when a tab has no captured data. */}
          {!t.transcript?.length && !hasLogs && !hasTimeline ? (
            <p className="text-[12.5px] text-faint">
              No conversation, console, or timeline data was captured for this
              session.
            </p>
          ) : null}
        </div>

        {/* ── Side rail (360px) ───────────────────────────────────── */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-6 lg:self-start">
          {/* Status controls */}
          <SideSection title="Status">
            <Field label="Status" htmlFor="status-select" className="gap-2">
              <Select
                id="status-select"
                value={t.status}
                onChange={(e) => changeStatus(e.target.value as TicketStatus)}
              >
                {TICKET_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace('_', ' ')}
                  </option>
                ))}
              </Select>
            </Field>
          </SideSection>

          {/* Reporter & context */}
          <SideSection title="Reporter & context">
            <KV k="Page" icon={<Icon.globe />}>
              {t.pageUrl ? (
                <a
                  href={t.pageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mono truncate text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  title={t.pageTitle || t.pageUrl}
                >
                  {t.pageTitle || t.pageUrl}
                </a>
              ) : (
                '—'
              )}
            </KV>
            {t.endUserEmail ? (
              <KV k="Email" icon={<Icon.mail />}>
                <span className="mono">{t.endUserEmail}</span>
              </KV>
            ) : null}
            <KV k="Language">{t.lang || '—'}</KV>
            {t.viewport ? (
              <KV k="Viewport" icon={<Icon.monitor />}>
                <span className="mono">
                  {t.viewport.width}×{t.viewport.height} @{t.viewport.dpr}x
                </span>
              </KV>
            ) : null}
            <KV k="User agent">
              <span
                className="mono truncate text-[11.5px]"
                title={t.userAgent || undefined}
              >
                {t.userAgent || '—'}
              </span>
            </KV>
            <KV k="Created" icon={<Icon.clock />}>
              {new Date(t.createdAt).toLocaleString()}
            </KV>
          </SideSection>

          {/* Screenshot */}
          {screenshot?.url ? (
            <SideSection title="Screenshot">
              <a
                href={screenshot.url}
                target="_blank"
                rel="noreferrer"
                className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <img
                  src={screenshot.url}
                  alt="Captured screenshot"
                  className="w-full rounded-md border border-border"
                />
              </a>
            </SideSection>
          ) : null}

          {/* User-attached files (images preview inline; others download) */}
          {files.length > 0 ? (
            <SideSection title="Attachments">
              <div className="flex flex-col gap-2">
                {files.map((f) =>
                  f.url && f.contentType.startsWith('image/') ? (
                    <a
                      key={f.id}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <img
                        src={f.url}
                        alt="Attachment"
                        className="w-full rounded-md border border-border"
                      />
                    </a>
                  ) : (
                    <a
                      key={f.id}
                      href={f.url ?? '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-sm border border-border px-2.5 py-2 text-[12.5px] text-muted transition hover:border-strong hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary [&_svg]:size-4"
                    >
                      <Icon.link />
                      <span className="truncate">{attachmentLabel(f)}</span>
                    </a>
                  ),
                )}
              </div>
            </SideSection>
          ) : null}

          {/* Internal thread */}
          <CommentThread sessionId={t.id} />

          {/* Reply to reporter */}
          <ReplyPanel ticket={t} />
        </aside>
      </div>
    </section>
  );
}

/* ── Side-rail building blocks (`.side-sec` / `.kv`) ───────────────────── */

/** Human label for a non-previewed attachment: type · size. */
function attachmentLabel(a: Attachment): string {
  const size =
    a.sizeBytes >= 1024 * 1024
      ? `${(a.sizeBytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(a.sizeBytes / 1024))} KB`;
  const type = a.contentType.includes('pdf')
    ? 'PDF'
    : (a.contentType.split('/')[1] ?? 'file').toUpperCase();
  return `${type} · ${size}`;
}

function SideSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): ReactElement {
  return (
    <Card>
      <Card.Body className="flex flex-col gap-3">
        <h4 className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-faint">
          {title}
        </h4>
        {children}
      </Card.Body>
    </Card>
  );
}

function KV({
  k,
  icon,
  children,
}: {
  k: string;
  icon?: ReactNode;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 text-[12.5px]">
      <span className="flex flex-shrink-0 items-center gap-[7px] whitespace-nowrap text-muted [&_svg]:size-[14px] [&_svg]:opacity-70">
        {icon}
        {k}
      </span>
      <span className="flex min-w-0 flex-1 justify-end overflow-hidden text-ellipsis whitespace-nowrap text-right font-semibold text-text">
        {children}
      </span>
    </div>
  );
}

/* ── Tab panels ────────────────────────────────────────────────────────── */

/** Conversation transcript — agent/tool/user bubbles (`.convo` / `.cline`). */
function Conversation({ ticket }: { ticket: Ticket }): ReactElement {
  const turns = ticket.transcript ?? [];
  if (turns.length === 0) {
    return <p className="text-[12.5px] text-faint">No transcript captured.</p>;
  }
  return (
    <div className="flex flex-col gap-3.5">
      {turns.map((turn, i) => {
        const isUser = turn.role === 'user';
        return (
          <div
            key={i}
            data-role={turn.role}
            className={cn(
              'flex max-w-[92%] flex-col gap-1',
              isUser ? 'items-end self-end' : 'items-start',
            )}
          >
            <span className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-faint">
              {turn.role !== 'user' ? <Icon.sparkle size={11} /> : null}
              {turn.role}
            </span>
            <span
              className={cn(
                'text-sm leading-[1.55]',
                isUser
                  ? 'rounded-[14px_14px_4px_14px] bg-primary px-3.5 py-[11px] text-primary-ink'
                  : 'rounded-[4px_14px_14px_14px] border border-border bg-surface-2 px-3.5 py-[11px] text-text',
              )}
            >
              {turn.text}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Console logs + captured JS errors (`.logs` / `.log-line`). */
function Console({ ticket }: { ticket: Ticket }): ReactElement {
  const logs = ticket.consoleLogs ?? [];
  const errors = ticket.errors ?? [];
  if (logs.length === 0 && errors.length === 0) {
    return (
      <p className="text-[12.5px] text-faint">No console output captured.</p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {logs.length > 0 ? (
        <div className="mono overflow-hidden rounded-md border border-border bg-surface-2 text-xs leading-[1.7]">
          {logs.map((c, i) => (
            <div
              key={i}
              data-level={c.level}
              className={cn(
                'grid grid-cols-[56px_1fr] gap-3 border-b border-border px-3.5 py-1 last:border-b-0',
                c.level === 'error' ? 'bg-danger-soft' : '',
              )}
            >
              <span
                className={cn(
                  'font-bold uppercase',
                  c.level === 'error'
                    ? 'text-danger'
                    : c.level === 'warn'
                      ? 'text-warn'
                      : c.level === 'info'
                        ? 'text-info'
                        : 'text-faint',
                )}
              >
                {c.level}
              </span>
              <span className="whitespace-pre-wrap break-words text-text">
                {c.args.join(' ')}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {errors.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {errors.map((e, i) => (
            <li key={i} className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-danger">
                {e.message}
              </span>
              {e.stack ? (
                <pre className="mono overflow-x-auto rounded-md border border-border bg-surface-2 p-2 text-xs text-muted">
                  {e.stack}
                </pre>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Action timeline — dotted rail of captured user actions (`.timeline`). */
function Timeline({ ticket }: { ticket: Ticket }): ReactElement {
  const actions = ticket.actions ?? [];
  if (actions.length === 0) {
    return <p className="text-[12.5px] text-faint">No actions captured.</p>;
  }
  return (
    <div className="relative pl-2">
      {actions.map((a, i) => {
        const isErr = /error|crash|fail/i.test(a.type);
        const last = i === actions.length - 1;
        return (
          <div
            key={i}
            data-type={a.type}
            className="relative grid grid-cols-[26px_1fr] gap-3 pb-4"
          >
            {!last ? (
              <span className="absolute left-[12.5px] top-[26px] bottom-0 w-[1.5px] bg-border" />
            ) : null}
            <span
              className={cn(
                'z-[1] grid h-[26px] w-[26px] place-items-center rounded-lg border [&_svg]:size-[13px]',
                isErr
                  ? 'border-transparent bg-danger-soft text-danger'
                  : 'border-border bg-surface-2 text-muted',
              )}
            >
              <Icon.mouse />
            </span>
            <div className="pt-[3px]">
              <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-text">
                <span>{a.type}</span>
                {a.target ? (
                  <code className="mono rounded bg-surface-2 px-1.5 py-0.5 text-xs font-bold text-text">
                    {a.target}
                  </code>
                ) : null}
                {a.value ? (
                  <span className="font-normal text-muted">{a.value}</span>
                ) : null}
              </div>
              <div className="mt-0.5 text-[11.5px] tabular-nums text-faint">
                {new Date(a.ts).toLocaleTimeString()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
